import uniqueId from "lodash/uniqueId";

const sendToMap = {
  program: "PROGRAM",
  banner: "BANNER",
  mixer: "MIXER",
  gallery: "GALLERY",
};

const overlaps = (segmentA, segmentB) => {
  return (
    (segmentA.start < segmentB.start && segmentA.end > segmentB.start) ||
    (segmentA.start < segmentB.end && segmentA.end > segmentB.end) ||
    (segmentA.start === segmentB.start && segmentA.end === segmentB.end)
  );
};

export const isOverlapped = ({ id, start, end, segments: allSegments }) => {
  const segments = allSegments.filter(segment => segment.id !== id);
  const segment = { start, end };

  return segments.some(s => overlaps(s, segment) || overlaps(segment, s));
};

export const DEFAULT_DURATION = 10;
export const MIN_DURATION = 1;
export const SECOND_IN_PIXELS = 20;
export const CURRENT_INDICATOR_WIDTH = 3;
export const AXIS_LEGEND_PERIOD = 5;

export const sortSegments = (a, b) => a.start - b.start || a.id - b.id;

export const secondsToPixels = seconds => seconds * SECOND_IN_PIXELS;
export const pixelsToSeconds = (pixels, round = true) =>
  round ? Math.round(pixels / SECOND_IN_PIXELS) : pixels / SECOND_IN_PIXELS;

export const validatePosition = ({ val, min, max }) => {
  if (max && val > max) {
    return max;
  }

  if (min && val < min) {
    return min;
  }

  return val;
};

export const getDragValues = ({ value, period, duration }) => {
  const start = validatePosition({
    val: value,
    min: 0,
    max: period - duration,
  });
  return { start, end: start + duration };
};

export const getResizeEndValue = ({ value, start, period }) =>
  validatePosition({
    val: start + value,
    min: start + MIN_DURATION,
    max: period,
  });

export const getEndValue = ({ value, period }) =>
  validatePosition({
    val: value,
    min: 0,
    max: period,
  });

// We map layouts to a custom Timeline entity with layout data + schedules data
// The problem here: The schedules data for each layout are inside the schedules entities,
//    and the layout data inside the graphicset.
export const getTimelineElements = ({ layouts, currentSchedule, displayOnly }) =>
  layouts.map(layout => {
    const currentSegments = layout.segments
      ? layout.segments.filter(segment => segment.type === currentSchedule.type)
      : [];

    // Elements and layous have now a customId
    // The problem here: The schedules and the layout are both mapped by "assetId" on schedule entity, which
    //    doesn't exist on new form, or if we choose a non-existent asset to upload, so we have to upload
    //    and map the "assetId" on backend
    return {
      layoutId: displayOnly ? layout.assetId : layout.id,
      showAlways: layout.sendTo === sendToMap.program,
      name: layout.asset && layout.asset.codename ? layout.asset.codename : layout.assetIdCodename || layout.id,
      segments: currentSegments.map(currentSegment => ({
        id: displayOnly ? uniqueId() : currentSegment.id,
        type: currentSegment.type,
        start: currentSegment ? currentSegment.start : 0,
        end: currentSegment ? currentSegment.end : DEFAULT_DURATION,
      })),
    };
  });

const checkIsValid = layout => {
  const validSendTos = [sendToMap.banner, sendToMap.program];
  const validFormats = ["png", "apng"];
  const validTypes = ["image"];

  return (
    (!layout.format || validFormats.includes(layout.format)) &&
    (!layout.type || validTypes.includes(layout.type)) &&
    (layout.sendTo && validSendTos.includes(layout.sendTo))
  );
};

export const getValidLayouts = layouts => (layouts ? layouts.filter(layout => checkIsValid(layout)) : []);

export const isOnTime = currentTime => element =>
  element.showAlways || element.segments.some(segment => segment.start <= currentTime && segment.end >= currentTime);

export const segmentReducer = (state, action) => {
  switch (action.type) {
    case "drag":
    case "start":
      return getDragValues({
        value: action.value,
        period: action.period,
        duration: state.end - state.start,
      });
    case "resize":
      return {
        ...state,
        end: getResizeEndValue({
          value: action.value,
          start: state.start,
          period: action.period,
        }),
      };
    case "end":
      return {
        ...state,
        end: getEndValue({
          value: action.value,
          period: action.period,
        }),
      };
    case "reset":
      return { ...state, start: action.start, end: action.end };
    default:
      throw new Error();
  }
};

export const formatSegmentLabel = ({ width, label }) => {
  if (width < 30 && label.length > 4) {
    return label.substring(0, 1) + "...";
  } else if (width < 50 && label.length > 4) {
    return label.substring(0, 2) + "...";
  } else {
    return label;
  }
};
